import {Tag} from '@wandb/weave/components/Tag';
import {clsx} from 'clsx';
import React, {type ReactNode} from 'react';

import {useDragOver} from './useDragOver';

type TableEditorColumnProps = {
  children: ReactNode;
  count: number;
  title: string;
  titleControls?: ReactNode;
  titleSuffix?: ReactNode;
  onDrop: (visible: boolean, pinned: boolean) => void;
  visible?: boolean;
};

export function TableEditorColumn({
  children,
  count,
  title,
  titleControls,
  titleSuffix,
  onDrop,
  visible,
}: TableEditorColumnProps) {
  const {getDraggingProps, isDraggingOver} = useDragOver({
    onDrop() {
      // Handle drops on any element that isn't part of a column section, so
      // probably the column header or some bit of empty space in the column.

      // If the column is visible, it should be both pinned and visible.
      // If column is hidden, it should be neither:
      onDrop(Boolean(visible), Boolean(visible));
    },
  });

  const cls = clsx(
    'basis-1/2 overflow-hidden rounded-md border border-wb-border',
    {
      'border-wb-accent': isDraggingOver,
    }
  );

  return (
    <div className={cls} {...getDraggingProps()}>
      <div className="flex h-40 items-center justify-between border-b border-wb-border bg-wb-surface-tint pl-16 pr-8">
        <div className="flex items-center gap-6">
          <div className="text-sm font-semibold uppercase text-wb-text-tertiary">
            {title}
          </div>
          <Tag color="moon" label={count.toLocaleString()} />
          {titleSuffix}
        </div>
        <div>{titleControls}</div>
      </div>
      <div className="flex h-[350px] flex-col overflow-y-auto">{children}</div>
    </div>
  );
}
